import React, { useContext } from "react";
import Grid from "@mui/material/Grid";
import Logo from "assets/brand/logo.png";
import { Box, Container, Typography } from "@mui/material";
import { LinkWithScroll } from "./LinkWithScroll";
import { Link } from "react-router-dom";
import data from "common/data/index.json";
import { LanguageContext } from "configuration/app-context-manager/LanguageContext";

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontFamily: "'Lato', sans-serif",
      fontWeight: 700,
      fontSize: "0.7rem",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      color: "var(--clr-primary)",
      mb: 2.5,
    }}
  >
    {children}
  </Typography>
);

const FooterLink = ({ children, ...props }) => (
  <Typography
    sx={{
      fontFamily: "'Lato', sans-serif",
      fontSize: "0.85rem",
      color: "rgba(252, 251, 249, 0.7)",
      transition: "color 0.3s ease",
      "&:hover": { color: "var(--clr-primary)" },
    }}
    {...props}
  >
    {children}
  </Typography>
);

export const Footer = () => {
  const { language } = useContext(LanguageContext);
  return (
    <Box mt="auto">
      {/* Gold separator line */}
      <Box
        sx={{
          width: "100%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, var(--clr-primary), transparent)",
        }}
      />

      <Box py={{ xs: 6, md: 8 }} sx={{ backgroundColor: "common.black" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 2 }}>
            {/* Logo */}
            <Grid item xs={12} md={3}>
              <Box
                height="100%"
                display="flex"
                alignItems={{ xs: "center", md: "flex-start" }}
                justifyContent={{ xs: "center", md: "flex-start" }}
              >
                <LinkWithScroll to={`/${language}`}>
                  <img src={Logo} alt="Logo Casinhas do Mé-Mé" width="90px" />
                </LinkWithScroll>
              </Box>
            </Grid>

            {/* Links */}
            <Grid item xs={12} md={3}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <SectionTitle>Links</SectionTitle>
                {data[language].footer.Links.map((page) => (
                  <LinkWithScroll
                    key={page.name}
                    to={`/${language}/${page.href}`}
                    style={{
                      textDecoration: "none",
                      marginBottom: "8px",
                    }}
                  >
                    <FooterLink>{page.name}</FooterLink>
                  </LinkWithScroll>
                ))}
              </Box>
            </Grid>

            {/* Social Media */}
            <Grid item xs={12} md={3}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <SectionTitle>
                  {language === "pt" ? "Redes Sociais" : "Social Media"}
                </SectionTitle>
                {data[language].footer.SocialMedia.map((sm) => (
                  <Link
                    key={sm.name}
                    to={sm.href}
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                    target="_blank"
                  >
                    <img
                      src={require(`assets/svg/${sm.icon}`)}
                      alt={sm.name}
                      width="16px"
                      style={{ opacity: 0.7 }}
                    />
                    <FooterLink ml={1}>{sm.name}</FooterLink>
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Contacts */}
            <Grid item xs={12} md={3}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <SectionTitle>
                  {language === "pt" ? "Contactos" : "Contacts"}
                </SectionTitle>
                {data[language].footer.Contacts.map((contact) => (
                  <Box key={contact.text} mb={1}>
                    <Box display="flex" alignItems="center">
                      <img
                        src={require(`assets/svg/${contact.icon}`)}
                        alt={contact.text}
                        width="14px"
                        style={{ opacity: 0.7 }}
                      />
                      {contact.email || contact.phone ? (
                        <a
                          href={
                            contact.email
                              ? `mailto:${contact.text}`
                              : `tel:+351${contact.text}`
                          }
                          style={{ textDecoration: "none" }}
                        >
                          <FooterLink ml={1}>{contact.text}</FooterLink>
                        </a>
                      ) : (
                        <FooterLink ml={1}>{contact.text}</FooterLink>
                      )}
                    </Box>
                    {contact.disclaimer && (
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "var(--clr-primary)",
                          ml: 3,
                          mt: 0.3,
                          opacity: 0.8,
                        }}
                      >
                        {contact.disclaimer}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Bottom bar */}
      <Box sx={{ backgroundColor: "#1a0103", py: 1.5 }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.65rem",
              letterSpacing: "0.06em",
              color: "rgba(252, 251, 249, 0.35)",
              textAlign: "center",
              mb: 0.5,
            }}
          >
            {language === "pt"
              ? "As Casinhas do Mé-Mé são exploradas pela empresa ACRIBIA II – COORDENAÇÃO, FISCALIZAÇÃO E ENGENHARIA, LDA."
              : "Casinhas do Mé-Mé is operated by ACRIBIA II – COORDENAÇÃO, FISCALIZAÇÃO E ENGENHARIA, LDA."}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Lato', sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.08em",
              color: "rgba(252, 251, 249, 0.4)",
              textAlign: "center",
            }}
          >
            {new Date().getFullYear()} &copy; All rights reserved &mdash; Casinhas do Mé-Mé &mdash;
            R. Joaquim Pereira de Matos, Manteigas &mdash;{" "}
            <a
              href="https://www.livroreclamacoes.pt/inicio/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "rgba(252, 251, 249, 0.6)",
                textDecoration: "none",
                transition: "color 0.3s ease",
              }}
              onMouseOver={(e) => (e.target.style.color = "var(--clr-primary)")}
              onMouseOut={(e) => (e.target.style.color = "rgba(252, 251, 249, 0.6)")}
            >
              {language === "pt" ? "Livro de Reclamações" : "Complaints Book"}
            </a>
            {" "}&mdash;{" "}
            <LinkWithScroll
              to={`/${language}/privacy`}
              style={{
                color: "rgba(252, 251, 249, 0.6)",
                textDecoration: "none",
                transition: "color 0.3s ease",
              }}
              onMouseOver={(e) => (e.target.style.color = "var(--clr-primary)")}
              onMouseOut={(e) => (e.target.style.color = "rgba(252, 251, 249, 0.6)")}
            >
              {language === "pt" ? "Política de Privacidade" : "Privacy Policy"}
            </LinkWithScroll>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
